import {
  IScheduleHandler,
  ScheduleContext,
  ScheduleEvaluationResult,
} from "../types/index";
import {
  Playlist,
  ScheduleType,
  GregorianDateSchedule,
  isGregorianDateSchedule,
  parseDaysString,
} from "../types";

/**
 * Gregorian Date Schedule Handler
 *
 * Handles playlists with ScheduleType.GregorianDate (6)
 * Active on specific Gregorian calendar dates.
 *
 * Examples:
 * - December 25 (Christmas)
 * - January 1 (New Year)
 * - July 4 (Independence Day)
 * - Specific date ranges: "1-7" (first week of month)
 */
export class GregorianDateScheduleHandler implements IScheduleHandler {
  public readonly scheduleType = ScheduleType.GregorianDate;

  /**
   * Evaluate if playlist is active based on Gregorian date
   */
  evaluate(
    playlist: Playlist,
    context: ScheduleContext
  ): ScheduleEvaluationResult {
    const schedule = playlist.schedule;

    if (!schedule || !isGregorianDateSchedule(schedule)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Invalid or missing GregorianDate schedule configuration",
      };
    }

    const currentMonth = context.currentTime.getMonth() + 1; // 1-12
    const currentDay = context.currentTime.getDate(); // 1-31
    const currentTime = this.getTimeString(context.currentTime);

    // Check if current month is in schedule
    if (!schedule.months.includes(currentMonth)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Not active in ${this.getMonthName(currentMonth)}`,
      };
    }

    // Parse days string
    const activeDays = parseDaysString(schedule.days);

    // Check if current day is in schedule
    if (!activeDays.includes(currentDay)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Not active on day ${currentDay} of ${this.getMonthName(
          currentMonth
        )}`,
      };
    }

    // If time is specified, check it
    if (schedule.time) {
      if (currentTime < schedule.time) {
        return {
          isActive: false,
          effectivePriority: playlist.priority,
          reason: `Not yet active (starts at ${schedule.time})`,
          nextEvaluationTime: this.parseTimeToDate(
            schedule.time,
            context.currentTime
          ),
        };
      }
    }

    // Calculate deactivation time (end of day or specific time)
    const deactivationTime = new Date(context.currentTime);
    deactivationTime.setHours(23, 59, 59, 999);

    return {
      isActive: true,
      effectivePriority: playlist.priority,
      deactivationTime,
      reason: `Active on ${this.getMonthName(currentMonth)} ${currentDay}${
        schedule.time ? ` at ${schedule.time}` : ""
      }`,
      nextEvaluationTime: deactivationTime,
    };
  }

  /**
   * Get next state change time
   */
  getNextStateChange(
    playlist: Playlist,
    context: ScheduleContext
  ): Date | null {
    const schedule = playlist.schedule;

    if (!schedule || !isGregorianDateSchedule(schedule)) {
      return null;
    }

    // Find next occurrence of this date
    const currentMonth = context.currentTime.getMonth() + 1;
    const currentDay = context.currentTime.getDate();

    const activeDays = parseDaysString(schedule.days);

    // Check if currently in an active date
    if (
      schedule.months.includes(currentMonth) &&
      activeDays.includes(currentDay)
    ) {
      // Return end of day
      const endOfDay = new Date(context.currentTime);
      endOfDay.setHours(23, 59, 59, 999);
      return endOfDay;
    }

    // Would need to calculate next occurrence (complex for year wrapping)
    return null;
  }

  /**
   * Validate configuration
   */
  validateConfiguration(playlist: Playlist): string[] {
    const errors: string[] = [];
    const schedule = playlist.schedule;

    if (!schedule || !isGregorianDateSchedule(schedule)) {
      errors.push("GregorianDate schedule configuration is missing or invalid");
      return errors;
    }

    const gregorianSchedule = schedule as GregorianDateSchedule;

    // Validate schedule type
    if (gregorianSchedule.type !== ScheduleType.GregorianDate) {
      errors.push(
        `Schedule type mismatch: expected ${ScheduleType.GregorianDate}, got ${gregorianSchedule.type}`
      );
    }

    // Validate months
    if (!schedule.months || schedule.months.length === 0) {
      errors.push("At least one month must be selected");
    }

    if (schedule.months.some((month) => month < 1 || month > 12)) {
      errors.push("Months must be between 1 (January) and 12 (December)");
    }

    // Validate days string
    if (!schedule.days || schedule.days.trim() === "") {
      errors.push("Days configuration is required");
    } else {
      try {
        const days = parseDaysString(schedule.days);
        if (days.length === 0) {
          errors.push("At least one day must be specified");
        }
        if (days.some((day) => day < 1 || day > 31)) {
          errors.push("Days must be between 1 and 31");
        }
      } catch (e) {
        errors.push(`Invalid days format: ${schedule.days}`);
      }
    }

    // Validate time if specified
    if (schedule.time && !this.isValidTimeFormat(schedule.time)) {
      errors.push(`Invalid time format: ${schedule.time}`);
    }

    return errors;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private getTimeString(date: Date): string {
    return date.toTimeString().split(" ")[0]; // "HH:MM:SS"
  }

  private parseTimeToDate(timeStr: string, baseDate: Date): Date {
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
    const result = new Date(baseDate);
    result.setHours(hours, minutes, seconds || 0, 0);
    return result;
  }

  private isValidTimeFormat(timeStr: string): boolean {
    if (!timeStr) return false;
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
    return timeRegex.test(timeStr);
  }

  private getMonthName(month: number): string {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[month - 1] || "Unknown";
  }
}
